/* Pezzi comuni all'apertura e alle schede dell'indice /report (modello del
   19/10/2026, uploads/report.html). Tutti Server Component. */

import Image from "next/image";

import { LogoOI } from "@/components/common/LogoOI";
import { Icona } from "@/components/home/Icona";
import { DATE, ETICHETTE } from "@/components/report/stile";
import { TIPO_DERIVATO } from "@/lib/constants";
import { TONO_ICONA, iconaCategoria } from "@/lib/prima-pagina";
import type { SchedaReport } from "@/lib/report-dati";

const tipoDi = (s: SchedaReport) => (s.tipoDerivato ? TIPO_DERIVATO[s.tipoDerivato].label : null);

/** La riga delle etichette e, a destra, «Accesso riservato» (`.riga`).
 *  Territorio, settore con la sua icona e tipo (Notizia / Caso / Report);
 *  un campo vuoto non lascia il separatore. L'etichetta d'accesso compare
 *  solo dove `free` è falso: il modello la scrive su tutte le schede
 *  perché oggi i report sono tutti riservati, qui si legge riga per riga. */
export function Etichette({ s }: { s: SchedaReport }) {
  const tipo = tipoDi(s);
  const voci = [
    s.territorio ? (
      <span key="t" className="inline-flex items-center gap-1.5">
        <Icona nome="paese" className="size-4 text-mare" />
        <b className="font-semibold text-ink">{s.territorio}</b>
      </span>
    ) : null,
    s.settore ? (
      <span key="s" className="inline-flex items-center gap-1.5">
        <Icona nome={iconaCategoria(s.settore)} tono={TONO_ICONA[iconaCategoria(s.settore)]} />
        {s.settore}
      </span>
    ) : null,
    tipo ? <span key="k">{tipo}</span> : null,
  ].filter(Boolean);
  return (
    <span className="flex flex-wrap items-center justify-between gap-x-3.5 gap-y-2">
      {voci.length > 0 ? (
        <span className={`inline-flex flex-wrap items-center gap-1.5 ${ETICHETTE}`}>
          {voci.map((v, i) => (
            <span key={i} className="inline-flex items-center gap-1.5">
              {i > 0 ? (
                <i aria-hidden className="mx-0.5 not-italic">
                  ·
                </i>
              ) : null}
              {v}
            </span>
          ))}
        </span>
      ) : (
        <span />
      )}
      {s.libero ? null : (
        <span className="inline-flex items-center gap-1.5 border border-dashed border-mare px-2 py-0.5 font-mono text-[11px] uppercase tracking-dato text-mare">
          <Icona nome="accedi" className="size-[13px]" />
          Accesso riservato
        </span>
      )}
    </span>
  );
}

/** «Pubblicato» e, solo se c'è, «Aggiornato» (`.dt`). `lunga`: la data per
 *  esteso dell'apertura. */
export function DateDossier({ s, lunga = false }: { s: SchedaReport; lunga?: boolean }) {
  return (
    <span className={DATE}>
      Pubblicato{" "}
      <time dateTime={s.pubblicato.iso} className="font-semibold text-ink">
        {lunga ? s.pubblicato.testo : s.pubblicato.breve}
      </time>
      {s.aggiornato ? (
        <>
          <i aria-hidden className="mx-2 not-italic">
            ·
          </i>
          Aggiornato{" "}
          <time dateTime={s.aggiornato.iso} className="font-semibold text-ink">
            {lunga ? s.aggiornato.testo : s.aggiornato.breve}
          </time>
        </>
      ) : null}
    </span>
  );
}

/** Il piede della scheda (`.go`): il segno, tipo, codice e settimana, e la
 *  freccia che si sposta al passaggio sulla scheda. */
export function PiedeDossier({ s }: { s: SchedaReport }) {
  const voci = [tipoDi(s), s.codice, `sett. ${s.settimana}`].filter(Boolean).join(" · ");
  return (
    <span className="mt-auto flex items-center justify-between pt-1">
      <span className={`inline-flex items-center gap-[7px] ${ETICHETTE}`}>
        <LogoOI variant="segno" decorative accento="fill-segnale" className="h-[13px] w-auto text-mare" />
        {voci}
      </span>
      <span
        aria-hidden
        className="font-sans text-corpo font-semibold text-mare transition-transform duration-200 group-hover/dossier:translate-x-0.5 group-hover/dossier:-translate-y-0.5 motion-reduce:transition-none"
      >
        ↗
      </span>
    </span>
  );
}

/** La foto del dossier in bicromia (`.duo`): in bianco e nero, schiarita
 *  in `screen` sul fondo del mare (`.oi-rp-duo` in globals.css). Senza
 *  `image_url` non si rende niente: nessuna foto d'archivio al posto
 *  sbagliato e nessun riempitivo. */
export function FotoDossier({
  foto,
  sizes,
  className = "",
  priorita = false,
}: {
  foto: string;
  sizes: string;
  className?: string;
  priorita?: boolean;
}) {
  return (
    <span className={`oi-rp-duo relative block overflow-hidden ${className}`}>
      <Image src={foto} alt="" fill sizes={sizes} preload={priorita} className="object-cover" />
    </span>
  );
}
